import { useEffect, useMemo, useRef, useState } from 'react';
import type { CoupangFulfillmentCenter } from '@auto-oms/shared/types/fulfillmentCenter';

interface Props {
  centers: CoupangFulfillmentCenter[];
  selectedCode: string;
  onSelect: (center: CoupangFulfillmentCenter) => void;
  disabled?: boolean;
}

export function FulfillmentCenterPicker({ centers, selectedCode, onSelect, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selected = centers.find((c) => c.code === selectedCode) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return centers;
    return centers.filter((c) => {
      const haystack = `${c.name} ${c.code} ${c.parcelAddress}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [centers, query]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-full items-center justify-between rounded-lg border border-ink-200 bg-paper px-3 text-left text-[13px] text-ink-900 transition-colors duration-150 hover:border-ink-300 focus:border-ink-900 focus:outline-none focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:bg-raised disabled:text-ink-400"
      >
        <span className={selected ? 'text-ink-900' : 'text-ink-400'}>
          {selected ? `${selected.name} (${selected.code})` : '물류센터 선택'}
        </span>
        <span className="ml-2 shrink-0 text-ink-400" aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-xl border border-ink-200 bg-paper shadow-pop">
          <div className="border-b border-ink-100 p-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="물류센터 이름·코드·주소 검색"
              className="input h-9 text-[13px]"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1 text-[13px]">
            {filtered.length === 0 ? (
              <li className="px-3 py-2.5 text-ink-400">검색 결과가 없습니다.</li>
            ) : (
              filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(c);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={[
                      'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors duration-150',
                      c.code === selectedCode
                        ? 'bg-coral-50 text-coral-700'
                        : 'text-ink-800 hover:bg-ink-50',
                    ].join(' ')}
                  >
                    <span className="min-w-[7rem] font-medium">
                      {c.name}
                      <span className="ml-1 text-[11px] text-ink-500">({c.code})</span>
                    </span>
                    <span className="truncate text-[11px] text-ink-500">{c.parcelAddress}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
